import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  useAuditLog, useAuditLogTables, useProfilesList, type AuditLogRow,
} from "@/lib/queries";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollText, ChevronLeft, ChevronRight, Funnel, X } from "lucide-react";
import { fmtDateTime, dateIST } from "@emr/shared/india";
import { PermissionGate } from "@/components/PermissionGate";
import { diff } from "@emr/shared";

function diffFields(oldData: Record<string, unknown> | null, newData: Record<string, unknown> | null): string {
  const fields = diff(oldData, newData);
  if (fields.length === 0) return "—";
  return fields.join(", ");
}

function actionBadge(action: string): "default" | "secondary" | "destructive" | "outline" {
  if (action === "INSERT") return "default";
  if (action === "UPDATE") return "secondary";
  if (action === "DELETE") return "destructive";
  return "outline";
}

export default function AuditLogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialUser = searchParams.get("user") ?? "";
  const initialTable = searchParams.get("tableName") ?? "";

  const [user, setUser] = useState(initialUser);
  const [tableName, setTableName] = useState(initialTable);
  // Lazy initializers keep clock reads out of render (purity).
  const [from, setFrom] = useState(() => dateIST(Date.now() - 7 * 24 * 3600 * 1000));
  const [to, setTo] = useState(() => dateIST());
  const [page, setPage] = useState(1);
  const limit = 50;

  useEffect(() => {
    const u = searchParams.get("user") ?? "";
    if (u && u !== user) setUser(u);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    setPage(1);
    const next = new URLSearchParams();
    if (user) next.set("user", user);
    if (tableName) next.set("tableName", tableName);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tableName]);

  const { data, isLoading, isFetching } = useAuditLog({
    user: user || undefined,
    tableName: tableName || undefined,
    from: from || undefined,
    to: to || undefined,
    page,
    limit,
  });
  const { data: tablesData } = useAuditLogTables();
  const { data: profiles } = useProfilesList();

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const clearFilters = () => {
    setUser("");
    setTableName("");
    setFrom(dateIST(Date.now() - 7 * 24 * 3600 * 1000));
    setTo(dateIST());
    setPage(1);
  };

  return (
    <PermissionGate perm="auditlog.view">
      <div className="mx-auto max-w-7xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500 flex items-center gap-2">
            <ScrollText size={20} /> Audit Log
          </h1>
          <div className="text-xs text-muted">{total} entr{total === 1 ? "y" : "ies"} · page {page} of {totalPages}</div>
        </div>

        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-5 items-end">
              <div>
                <Label className="text-xs">User</Label>
                <Select value={user || "all"} onValueChange={(v) => setUser(v === "all" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="All users" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All users</SelectItem>
                    {(profiles ?? []).map((p) => (
                      <SelectItem key={p.profileId} value={p.profileId}>{p.fullName} ({p.role})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Entity</Label>
                <Select value={tableName || "all"} onValueChange={(v) => setTableName(v === "all" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="All tables" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All tables</SelectItem>
                    {(tablesData?.tables ?? []).map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">From</Label>
                <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">To</Label>
                <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
              </div>
              <div>
                <Button variant="outline" onClick={clearFilters} className="w-full">
                  <X size={14} className="mr-1" /> Clear
                </Button>
              </div>
            </div>
            {isFetching && <div className="mt-2 text-xs text-muted">Refreshing…</div>}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead>Fields changed</TableHead>
                  <TableHead>IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={6}><Skeleton className="h-10" /></TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-sm text-muted py-10">
                      <Funnel size={32} className="mx-auto mb-2" />
                      No audit entries match these filters.
                    </TableCell>
                  </TableRow>
                ) : rows.map((r: AuditLogRow) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs whitespace-nowrap">{fmtDateTime(r.changedAt)}</TableCell>
                    <TableCell className="font-medium text-sm">
                      {r.changedByName ?? "—"}
                      {/* Dual-layer audit (writeAudit + audit_trigger): rows
                          with changed_by IS NULL were written by the DB
                          trigger, not the Hono writeAudit layer — flag them
                          so the two layers are distinguishable in the log
                          (the "duplicate" pairs are intentional; see §9).
                          changedBy (not changedByName) is the authoritative
                          test: changedByName is also null when an app row's
                          profile was since deleted (LEFT JOIN miss). */}
                      {r.changedBy === null && (
                        <Badge variant="outline" className="ml-1.5 border-amber-500/60 text-[9px] font-mono text-amber-700 dark:text-amber-500" title="Written by the Postgres audit trigger (changed_by IS NULL), not the app's writeAudit layer">
                          DB trigger
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={actionBadge(r.action)} className="text-[10px] font-mono">
                        {r.action}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      <span className="font-mono">{r.tableName}</span>
                      {r.recordId && <span className="ml-2 text-xs text-muted">#{r.recordId.slice(0, 8)}</span>}
                    </TableCell>
                    <TableCell className="text-xs text-muted">
                      {diffFields(r.oldData, r.newData)}
                    </TableCell>
                    <TableCell className="text-xs text-muted font-mono">{r.ip ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}>
            <ChevronLeft size={14} className="mr-1" /> Prev
          </Button>
          <Button variant="outline" onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages}>
            Next <ChevronRight size={14} className="ml-1" />
          </Button>
        </div>
      </div>
    </PermissionGate>
  );
}
